import { z } from 'zod';
import { ModelSlug } from './fields.js';
import { Uuid } from './primitives.js';
// AI console and eval contracts: routes, models, prompts, budget, spend, FX and evals.
// Dates travel as ISO strings; money in paise (integer) unless the field says otherwise.
/** Deterministic graders; no substring match is treated as educational correctness. */
export const EvalGrading = z.discriminatedUnion('kind', [
    z
        .object({
        kind: z.literal('exact'),
        alternatives: z.array(z.string().min(1).max(500)).max(20).default([]),
    })
        .strict(),
    z.object({ kind: z.literal('numeric'), tolerance: z.number().min(0).max(1).default(0) }).strict(),
    z
        .object({
        kind: z.literal('structured'),
        alternatives: z.array(z.string().min(1).max(500)).max(20).default([]),
        explanations: z.array(z.string().min(1).max(4000)).min(1).max(20),
        ambiguous: z.boolean().default(false),
        citations: z.array(z.string().min(1).max(500)).max(20),
    })
        .strict(),
]);
export const EvalCase = z
    .object({
    prompt: z.string().min(1).max(8000),
    system: z.string().max(4000).optional(),
    expected: z.string().min(1).max(500),
    grading: EvalGrading.optional(),
})
    .strict()
    .superRefine((c, ctx) => {
    if (c.grading?.kind === 'numeric' &&
        (!c.expected.trim() || !Number.isFinite(Number(c.expected))))
        ctx.addIssue({
            code: 'custom',
            path: ['expected'],
            message: 'Numeric grading needs a finite numeric expected answer',
        });
});
// ---- AI console ------------------------------------------------------------------------------
/** A paused AI channel after a billing or auth refusal (out of credit, bad key). */
export const ProviderHoldDto = z.object({
    channel: z.enum(['chat', 'embed']),
    kind: z.string(),
    message: z.string(),
    attempts: z.number(),
    startedAt: z.string(),
    retryAt: z.string(),
});
export const AiStatusDto = z.object({
    chatConfigured: z.boolean(),
    embeddingsConfigured: z.boolean(),
    embeddingsProvider: z.string().nullable(),
    embeddingDims: z.number(),
    storageDriver: z.string(),
    billingProductId: Uuid.nullable(),
    /** Empty while every channel runs normally. */
    providerHolds: z.array(ProviderHoldDto),
});
export const RouteParamsSchema = z
    .object({
    temperature: z.number().min(0).max(2).optional(),
    max_tokens: z.number().int().min(1).max(32_000).optional(),
    timeout_ms: z.number().int().min(1_000).max(120_000).optional(),
    cache_ttl_seconds: z
        .number()
        .int()
        .min(0)
        .max(30 * 86_400)
        .optional(),
    dimensions: z.number().int().positive().optional(),
})
    .strict();
export const AiRouteDto = z.object({
    id: Uuid,
    productId: Uuid.nullable(),
    task: z.string(),
    profile: z.string().nullable(),
    budgetClass: z.string().nullable(),
    primaryModel: z.string(),
    fallbackModels: z.array(z.string()),
    params: RouteParamsSchema,
    version: z.number(),
    updatedAt: z.string(),
    isOverride: z.boolean(),
});
export const SetRouteRequest = z
    .object({
    productId: Uuid.nullable(),
    primaryModel: ModelSlug,
    fallbackModels: z.array(ModelSlug).max(3),
    params: RouteParamsSchema.default({}),
})
    .strict();
export const AiTaskDto = z.object({
    name: z.string(),
    kind: z.enum(['chat', 'embed']),
    budget: z.enum(['user', 'pipeline']),
    profile: z.string(),
    maxTokens: z.number(),
    timeoutMs: z.number(),
    cacheTtlSeconds: z.number(),
    description: z.string(),
});
export const AiModelDto = z.object({
    id: Uuid,
    slug: z.string(),
    family: z.string(),
    modality: z.enum(['text', 'vision', 'embed']),
    inputPriceUsdPerMtok: z.number().nullable(),
    outputPriceUsdPerMtok: z.number().nullable(),
    contextLength: z.number().nullable(),
    embeddingDims: z.number().nullable(),
    supportsJsonSchema: z.boolean(),
    active: z.boolean(),
    pricesRefreshedAt: z.string().nullable(),
});
export const AddModelRequest = z
    .object({
    slug: ModelSlug,
    family: z.string().trim().min(1).max(60).optional(),
    embeddingDims: z.number().int().positive().optional(),
})
    .strict();
export const SetModelActiveRequest = z.object({ active: z.boolean() }).strict();
export const PromptTemplateDto = z.object({
    key: z.string(),
    version: z.number(),
    locale: z.string(),
    system: z.string(),
    template: z.string(),
    outputSchema: z.record(z.string(), z.unknown()).nullable(),
});
export const PromptVersionDto = PromptTemplateDto.extend({
    id: Uuid,
    status: z.string(),
    notes: z.string().nullable(),
    createdBy: Uuid.nullable(),
    createdAt: z.string(),
});
export const PromptListDto = z.object({
    defaults: z.array(PromptTemplateDto),
    versions: z.array(PromptVersionDto),
});
export const CreatePromptRequest = z
    .object({
    key: z.string().trim().min(1).max(80),
    locale: z.string().trim().min(2).max(10).optional(),
    system: z.string().min(1).max(20_000),
    template: z.string().min(1).max(20_000),
    notes: z.string().max(2000).optional(),
})
    .strict();
export const BudgetStatusDto = z.object({
    scope: z.enum(['user', 'product', 'content']),
    period: z.enum(['day', 'month']),
    periodStart: z.string(),
    limitPaise: z.number().nullable(),
    spentPaise: z.number(),
    reservedPaise: z.number(),
    pct: z.number().nullable(),
});
export const SpendDto = z.object({
    rows: z.array(z.object({
        day: z.string(),
        task: z.string(),
        model: z.string(),
        calls: z.number(),
        cacheHits: z.number(),
        failed: z.number(),
        refused: z.number(),
        costPaise: z.number(),
    })),
    totalPaise: z.number(),
    calls: z.number(),
    cacheHitRate: z.number().nullable(),
});
export const AiCallDto = z.object({
    id: Uuid,
    productId: Uuid.nullable(),
    task: z.string(),
    budgetClass: z.string(),
    modelRequested: z.string(),
    modelUsed: z.string().nullable(),
    promptTokens: z.number().nullable(),
    completionTokens: z.number().nullable(),
    cachedTokens: z.number().nullable(),
    estimatePaise: z.number(),
    costPaise: z.number().nullable(),
    costUsd: z.union([z.number(), z.string()]).nullable(),
    costSource: z.string(),
    latencyMs: z.number().nullable(),
    status: z.string(),
    cacheHit: z.boolean(),
    errorCode: z.string().nullable(),
    needsReconcile: z.boolean(),
    createdAt: z.string(),
});
export const AiAlertDto = z.object({
    id: Uuid,
    type: z.string(),
    payload: z.record(z.string(), z.unknown()),
    occurredAt: z.string(),
});
export const FxRateDto = z.object({
    date: z.string().nullable(),
    usdInr: z.number(),
    source: z.string(),
});
export const SetFxRequest = z.object({ usdInr: z.number().min(50).max(200) }).strict();
export const SpendQuery = z
    .object({ days: z.coerce.number().int().min(1).max(92).optional(), productId: Uuid.optional() })
    .strict();
export const CallsQuery = z
    .object({
    task: z.string().max(80).optional(),
    status: z.enum(['reserved', 'succeeded', 'failed', 'refused', 'cached']).optional(),
    before: z.string().max(40).optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
})
    .strict();
export const AddEvalCasesRequest = z
    .object({
    setKey: z.string().trim().min(1).max(80),
    task: z.string().trim().min(1).max(80),
    cases: z.array(EvalCase).min(1).max(100),
})
    .strict();
export const RunEvalRequest = z
    .object({ setKey: z.string().trim().min(1).max(80), model: ModelSlug })
    .strict();
//# sourceMappingURL=ai.js.map